import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TrilateralMap from "@/components/TrilateralMap";
import { ComerciosCercanos } from "@/components/ComerciosCercanos";
import { Badge } from "@/components/ui/badge";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  Package, Clock, Truck, CheckCircle2, MapPin, Radio, ShieldCheck,
} from "lucide-react";

const TIMELINE = [
  { key: "Pendiente", label: "Pedido Confirmado", Icon: CheckCircle2 },
  { key: "Preparando", label: "Tiendas Empacando", Icon: Package },
  { key: "En_Ruta", label: "Repartidor en Camino", Icon: Truck },
  { key: "Entregado", label: "Entregado", Icon: ShieldCheck },
];

function progressIdx(estado) {
  const map = { Pendiente: 0, Preparando: 1, Listo_Para_PickUp: 1, En_Ruta: 2, Entregado: 3 };
  return map[estado] ?? 0;
}

export default function CustomerDashboard() {
  const [ordenes, setOrdenes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const load = () => api.get("/ordenes/mias").then(r => {
      setOrdenes(r.data.ordenes);
      if (!selectedId && r.data.ordenes[0]) setSelectedId(r.data.ordenes[0].id);
    }).catch(e => toast.error(formatApiError(e)));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = ordenes.find(o => o.id === selectedId);

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline text-zinc-500">Mi cuenta</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">Mis pedidos</h1>

        <div className="mt-6"><ComerciosCercanos /></div>

        {ordenes.length === 0 ? (
          <div className="mt-10 border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">
            Aún no tienes pedidos
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-[320px_1fr] gap-6">
            {/* List */}
            <div className="space-y-2">
              {ordenes.map(o => (
                <button
                  key={o.id}
                  data-testid={`order-list-${o.id}`}
                  onClick={() => setSelectedId(o.id)}
                  className={`w-full text-left border rounded-sm p-4 bg-white transition-colors ${
                    o.id === selectedId ? "border-[#FF3366] ring-1 ring-[#FF3366]" : "border-zinc-200 hover:border-zinc-900"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold">{o.token_seguridad}</span>
                    <Badge className="bg-zinc-100 text-zinc-700 border-0 text-[10px]">{o.estado}</Badge>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-display font-bold mt-1">${o.total.toFixed(2)}</div>
                </button>
              ))}
            </div>

            {/* Detail */}
            {selected && <OrderTracking orden={selected} />}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderTracking({ orden }) {
  const activeIdx = progressIdx(orden.estado);
  const [driverPos, setDriverPos] = useState(null); // {lat,lng,at}
  const [tracking, setTracking] = useState(null);
  const wsRef = useRef(null);

  // Connect WebSocket when En_Ruta
  useEffect(() => {
    if (orden.estado !== "En_Ruta") { closeWs(); return; }
    let closed = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/ws-token");
        const url = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/rastreo?orden_id=${orden.id}&token=${encodeURIComponent(data.token)}`;
        const ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "posicion") setDriverPos({ lat: msg.lat, lng: msg.lng, at: msg.at });
          } catch { /* ignore */ }
        };
        ws.onerror = () => {};
        ws.onclose = () => { wsRef.current = null; };
        if (closed) { try { ws.close(); } catch { /* ignore */ } return; }
        wsRef.current = ws;
      } catch { /* ignore */ }
    })();
    return () => { closed = true; closeWs(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orden.id, orden.estado]);

  // Fase 9+10: trilateral tracking (fetch driver + stores + buyer)
  useEffect(() => {
    if (orden.estado !== "En_Ruta") return;
    const load = async () => {
      try {
        const { data } = await api.get(`/ordenes/${orden.id}/tracking-points`);
        if (driverPos) data.driver = { ...(data.driver || {}), lat: driverPos.lat, lng: driverPos.lng };
        setTracking(data);
      } catch (e) { console.error("tracking-points fetch failed:", e); }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [orden.id, orden.estado, driverPos?.lat, driverPos?.lng]);

  function closeWs() {
    if (wsRef.current) { try { wsRef.current.close(); } catch { /* ignore */ } wsRef.current = null; }
  }

  return (
    <div className="space-y-5">
      {/* Progress bar */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="overline text-zinc-500">Estado</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-0.5">
              {TIMELINE[activeIdx]?.label || orden.estado}
            </div>
          </div>
          <div className="text-right">
            <div className="overline text-zinc-500">Total</div>
            <div className="font-display font-black text-2xl tracking-tight">${orden.total.toFixed(2)}</div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute top-4 left-4 right-4 h-1 bg-zinc-200 rounded-sm" />
          <div
            className="absolute top-4 left-4 h-1 bg-[#FF3366] rounded-sm transition-all duration-500"
            style={{ width: `calc(${(activeIdx / (TIMELINE.length - 1)) * 100}% - ${(activeIdx / (TIMELINE.length - 1)) * 32}px)` }}
          />
          <div className="relative grid grid-cols-4 gap-2">
            {TIMELINE.map((s, i) => {
              const done = i <= activeIdx;
              const Icon = s.Icon;
              return (
                <div key={s.key} className="flex flex-col items-center" data-testid={`timeline-${s.key}`}>
                  <div className={`w-8 h-8 rounded-full grid place-items-center border-2 bg-white transition-colors ${
                    done ? "border-[#FF3366] text-[#FF3366]" : "border-zinc-300 text-zinc-400"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className={`text-[11px] mt-2 text-center font-medium ${done ? "text-zinc-900" : "text-zinc-400"}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Match seguro */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="bg-zinc-900 p-4 rounded-sm grid place-items-center">
            <QRCodeCanvas
              value={`en90min:${orden.id}:${orden.token_seguridad}`}
              size={140}
              bgColor="#09090B"
              fgColor="#D4FF00"
              level="M"
              data-testid="order-qr"
            />
          </div>
          <div>
            <div className="overline text-zinc-500">Match seguro</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1">Entrega este código a tu repartidor</div>
            <div className="mt-3 flex items-center gap-2">
              {orden.token_seguridad.split("").map((d, i) => (
                <div key={`${orden.id}-tok-${i}`} data-testid={`token-char-${i}`} className="w-14 h-16 grid place-items-center bg-zinc-50 border border-zinc-200 rounded-sm font-mono font-black text-4xl tracking-tighter">
                  {d}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-zinc-500 max-w-md">Al recibir tu paquete, comparte los 4 dígitos o el QR. Es la única forma de confirmar la entrega.</p>
          </div>
        </div>
      </div>

      {/* Live tracking */}
      {orden.estado === "En_Ruta" && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <div className="overline text-zinc-500 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Rastreo en vivo
              </div>
              <div className="font-display font-bold text-lg tracking-tight mt-0.5">Tu repartidor se dirige a ti</div>
            </div>
            {driverPos ? (
              <div className="text-right text-xs text-zinc-500">
                <div className="flex items-center gap-1"><Radio className="w-3 h-3 text-emerald-500" /> Última señal</div>
                <div className="font-mono">{new Date(driverPos.at).toLocaleTimeString()}</div>
              </div>
            ) : (
              <div className="text-xs text-zinc-400">Esperando señal GPS…</div>
            )}
          </div>
          <div className="aspect-[16/10] bg-zinc-100">
            <TrilateralMap
              driver={tracking?.driver || (driverPos ? { lat: driverPos.lat, lng: driverPos.lng, nombre: "Repartidor" } : null)}
              stores={tracking?.stores || []}
              buyer={tracking?.buyer}
              height="h-full"
            />
          </div>
          {driverPos && (
            <div className="p-4 flex items-center gap-2 text-xs text-zinc-600 border-t border-zinc-100">
              <MapPin className="w-3.5 h-3.5 text-[#FF3366]" />
              <span className="font-mono">{driverPos.lat.toFixed(5)}, {driverPos.lng.toFixed(5)}</span>
            </div>
          )}
        </div>
      )}

      {/* Detalles */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="overline text-zinc-500 mb-3">Detalle del pedido</div>
        <div className="grid gap-1.5 text-sm">
          {orden.detalles?.map((d, i) => (
            <div key={i} className="flex justify-between text-zinc-700">
              <span>{d.cantidad}× {d.nombre}</span>
              <span>${(d.cantidad * d.precio_unitario).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between text-xs text-zinc-500 mt-2 pt-2 border-t border-zinc-100">
            <span>Envío</span><span>${orden.costo_envio?.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
